import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Pagination } from "antd";
import GroupIcon from "../../assets/icons/Group.svg";
import DeleteIcon from "../../assets/icons/Delete.svg";
import EditIcon from "../../assets/icons/Edit.svg";
import { profileStatus } from "../../constants/commonConstants";

import LandLordTable from "../../components/Table/LandLordTable";
import TableHeader from "../../components/Common/TableHeader";
import DetailPopover from "../../components/Common/DetailPopover";
import Loader from "../../components/Common/Loader";
import NotFound from "../../components/NotFound";
import showToast from "../../utils/toastNotifications";

import { useThunk } from "../../hooks/useThunk";
import { fetchLandlords } from "../../store";
import { deleteUser } from "../../store";
import ConfirmationModal from "../../components/Model/ConfirmationModal";

const ManageLandlordsPage = () => {
  const [doFetchLandlords, isFetchingLandlords, fetchLandlordsError] =
    useThunk(fetchLandlords);
  const [doDeleteUser, isDeletingUser, deleteUserError] = useThunk(deleteUser);
  const [landlords, setLandlords] = useState([]);
  const [pagination, setPagination] = useState({});
  const [isEmptyData, setIsEmptyData] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentPage = parseInt(searchParams.get("page")) || 1;
  const searchTerm = searchParams.get("search") || "";
  const [search, setSearch] = useState(searchTerm);

  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const fetchData = async (page = currentPage, keyword = search) => {
    const query = {
      page,
      limit: itemsPerPage,
      ...(keyword && { search: keyword }),
    };

    const response = await doFetchLandlords(query);
    const data = response?.response?.data;
    const paginations = response?.response?.pagination || {};

    if (response?.success) {
      setLandlords(data);
      setIsEmptyData(response?.response?.data?.length === 0);
      setPagination(paginations);
    }
  };

  const handlePageChange = (page) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", page);
      if (search) newParams.set("search", search);
      return newParams;
    });
  };

  const handleSearch = () => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("search", search);
      newParams.set("page", 1);
      return newParams;
    });
  };

    const handleResetSearch = () => {
    setSearch("");
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.delete("search");
      newParams.set("page", 1);
      return newParams;
    });
  };

  const openDeleteModal = (userId) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
  };

  const handleDeleteUser = async () => {
    const response = await doDeleteUser(selectedUserId);

    if (response?.success) {
      fetchData();
      showToast("success", "Landlord deleted successfully");
    } else {
      showToast(
        "error",
         deleteUserError?.message || "Failed to delete landlord"
        );
    }
    setIsModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
  };

  const columns = [
    {
      title: "Landlord’s Name",
      dataIndex: "firstName",
      key: "name",
      render: (_, record) => (
        <span className="font-normal text-[#707070]">
          {record?.firstName} {record?.lastName}
        </span>
      ),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      render: (text) => (
        <span className={`${!text ? "block text-center" : ""}`}>
          {text || "-"}
        </span>
      ),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      render: (text) => (
        <span className={`${!text ? "block text-center" : ""}`}>
          {text || "-"}
        </span>
      ),
    },
    {
      title: "# of Properties",
      dataIndex: "propertyCount",
      key: "propertyCount",
      align: "center",
    },
    {
      title: "Total Units",
      dataIndex: "totalUnits",
      key: "totalUnits",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          className={`font-normal text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] leading-6 font-inter ${
            status?.[0] === profileStatus.ACTIVE
              ? "text-[#07C01D]"
              : "text-[#FF0505]"
          }`}
        >
          {status?.[0].charAt(0).toUpperCase() +
            status?.[0].slice(1).toLowerCase()}
        </span>
      ),
      align: "center",
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div className="flex justify-center gap-2">
          <DetailPopover
            // title="Landlord Profile"
            text={`View landlord properties ${record.firstName} ${record.lastName}`}
            placement="top"
            outerContainerStyle="rounded-[8px]"
          >
            <button className="bg-transparent text-[#07C01D] px-[6px] sm:px-2 md:px-[10px] lg:px-3 py-[2px] flex items-center  justify-center text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] font-inter font-normal leading-6 border border-[#07C01D] w-[55px] sm:w-[65px] md:w-[70px] lg:w-[75px] h-6 sm:h-[25px] md:h-[26px] lg:h-7 rounded-[15px] hover:bg-[#dffbe2]"
                           // onClick={() => {navigate(`/landlod/admin-manage-landload/profile/${record._id}/view-details/${user_id}`);}}
              onClick={() => { navigate(`/landlords/admin-manage-landload/view-properties/${record._id}`, {
                  state: { landlordName: `${record.firstName} ${record.lastName}` }
                });
              }}
            >

              <img
                src={GroupIcon}
                alt="Group icon"
                className="w-[7px] sm:w-2 md:w-[10px] lg:w-3 h-[7px] sm:h-[7px] md:h-[7px] lg:h-[7.33px] mr-[5px]"
              />
              View
            </button>
          </DetailPopover>
          <DetailPopover
            // title="Landlord Profile"
            text={`View full landlord profile ${record.firstName} ${record.lastName}`}
            placement="top"
            outerContainerStyle="rounded-[8px]"
          >
            <button className="bg-transparent text-[#57209D] px-[6px] sm:px-2 md:px-[10px] lg:px-3 py-[2px] flex items-center  justify-center text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] font-inter font-normal leading-6 border border-[#57209D] w-[105px] sm:w-[115px] md:w-[120px] lg:w-[125px] h-6 sm:h-[25px] md:h-[26px] lg:h-7 rounded-[15px] hover:bg-[#fce9ff]"
              onClick={() => {navigate(`/landlords/admin-manage-landload/profile/${record._id}`);}}>
              <img
                src={EditIcon}
                alt="Group icon"
                className="w-[7px] sm:w-2 md:w-[10px] lg:w-3 h-[7px] sm:h-[7px] md:h-[7px] lg:h-[7.33px] mr-[5px]"
                style={{ color: '#57209D', borderColor: '#57209D' }}/>
              View Landlod
            </button>
          </DetailPopover>
          <button
            className="bg-transparent text-[#FF0505] px-[6px] sm:px-2 md:px-[10px] lg:px-3 py-[2px] flex items-center justify-center text-[8px] sm:text-[10px] md:text-[11px] lg:text-[12px] font-inter font-normal leading-6 border border-[#FF0505] w-[60px] sm:w-[70px] md:w-[75px] lg:w-[80px] h-6 sm:h-[25px] md:h-[26px] lg:h-7 rounded-[15px] hover:bg-red-100"
            onClick={() => openDeleteModal(record._id)}
          >
            <img
              src={DeleteIcon}
              alt="Delete Icon"
              className="w-[11px] h-[11px] sm:w-[12px] sm:h-[12px] md:w-[13px] md:h-[13px] lg:w-[14px] lg:h-[14px] mr-[5px]"
            />
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-[30px]">
        <TableHeader
          searchValue={search}
          onSearchChange={setSearch}
          onSearchSubmit={handleSearch}
          searchPlaceholder="Search by name, email"
          buttons={[
            {
              label: "SEARCH",
              onClick: handleSearch,
              variant: "main",
              className:
                "bg-[#873FE2] rounded-[3.77px] h-[45px] w-[83px] border border-[#873FE2] text-[12px] uppercase font-[500] font-inter leading-[100%] text-white ",
            },
            ...(search ? [
            {
              label: "Clear",
              onClick: handleResetSearch,
              variant: "custom",
              className:
                "bg-white rounded-[3.77px] h-[45px] w-[83px] border border-[#873FE2] text-[12px] uppercase font-normal font-inter leading-[100%] text-[#873FE2] hover:bg-[#F7F0FF] focus:ring-2 focus:ring-[#873FE2] focus:ring-offset-2",
            },
            ] : []),
          ]}
          containerClass="bg-white grid grid-cols-1 md:flex w-full p-3 sm:p-4 md:p-6 lg:p-[30px] gap-3 md:gap-4 lg:gap-[20px] shadow-[0px_4px_4px_0px_#0000000F]"
          searchInputClass="text-gray-700 h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] sm:w-[427px]"
          buttonWrapperClass="justify-start"
          buttonClass="bg-[#8A65FF] text-white"
        />
      </div>

      <div className="bg-white rounded-[8px] border border-[#E5E5E5]">
        {isFetchingLandlords ? (
          <div className="relative top-0 mt-48 left-0 right-0 bottom-0 flex justify-center items-center">
            <Loader color="#4772F1" />
          </div>
        ) : isEmptyData ? (
          <NotFound
            text1="No data found"
            text2="No data match your current filter criteria."
            isButton={false}
          />
        ) : fetchLandlordsError ? (
          <NotFound
            text1="Error fetching data"
            text2={
              fetchLandlordsError.message ||
               "An error occurred while fetching data."
              }
            isButton={false}
          />
        ) : (
          <div>
          <LandLordTable
            dataSource={landlords}
            columns={columns}
            rowKey="key"
            hasSubTable={false}
            headingTextColor="#4772F1"
          />
          </div>
        )}
      </div>
      {/* Pagination */}
      {!isFetchingLandlords && !isEmptyData && !fetchLandlordsError && (
        <div className="flex justify-center">
          <Pagination
            current={pagination?.currentPage || 1}
            total={pagination?.totalRecords || 0}
            pageSize={itemsPerPage}
            onChange={handlePageChange}
            showSizeChanger={false}
            hideOnSinglePage={false}
            showLessItems
            className="custom-pagination"
          />
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpen}
        options={{
          title: "Are you sure?",
          description: "This action cannot be undone.",
          confirmText: "Delete",
          cancelText: "Cancel",
        }}
        onConfirm={handleDeleteUser}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};

export default ManageLandlordsPage;
